import { ReactNode, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useAgent from 'data/firestore/agent/use';
import useHttpQuery from 'data/firestore/useHttpQuery';
import useUserRoles from 'hooks/useUserRoles';
import { ACTIONS_BASE_URL } from '../../constants';
import { OrganizationEnabledAppsEnvelope, Provider } from './context';

interface Params {
    children?: ReactNode | ReactNode[];
}

const AgentProvider = ({ children }: Params) => {
    const { agentId = '' } = useParams<{ agentId?: string }>();
    const { role, isAdmin, isLoading: isLoadingUser } = useUserRoles();
    const canAccessAgent = Boolean(agentId)
        && !isLoadingUser
        && (isAdmin || ['viewer', 'editor', 'owner'].includes(role || ''));

    const { data: agentData, isLoading: isLoadingAgent } = useAgent(
        agentId,
        {
            disabled: !canAccessAgent,
            track: [agentId],
        },
    );

    const {
        data: enabledAppsResult,
        isLoading: isLoadingEnabledApps,
    } = useHttpQuery<OrganizationEnabledAppsEnvelope>({
        pathname: `/organizations/${agentId}/apps`,
        server: ACTIONS_BASE_URL,
        key: ['organization-apps', agentId],
        queryOptions: {
            enabled: canAccessAgent,
            retry: false,
        },
    });

    const enabledApps = useMemo(
        () => enabledAppsResult?.data.apps || [],
        [enabledAppsResult],
    );

    const isAppEnabled = useCallback((appKey: string) => (
        enabledApps.some((app) => app.appKey === appKey || app.slug === appKey)
    ), [enabledApps]);

    const value = useMemo(() => ({
        agent: agentData,
        isLoading: isLoadingUser || isLoadingAgent,
        enabledApps,
        isLoadingEnabledApps,
        isAppEnabled,
    }), [
        agentData,
        enabledApps,
        isAppEnabled,
        isLoadingAgent,
        isLoadingEnabledApps,
        isLoadingUser,
    ]);

    return (
        <Provider value={value}>
            {children}
        </Provider>
    );
};

export default AgentProvider;
